import "./lib/zod-config";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ApiError } from "@bridewell/api-client";
import { ToastProvider } from "./components/ui/toast";
import { ConsentProvider } from "./lib/consent";
import { installErrorReporting, reportError, setReportScreen } from "./lib/error-reporting";
import { currentLocale, LOCALE_TAG } from "./lib/locales";
import { ThemeProvider } from "./lib/theme";
import { isTokenPage } from "./lib/utils";
import { routeTree } from "./routeTree.gen";
import "./styles/app.css";

// Failures of this page's own code reach the server's error log (src/lib/error-reporting.ts). First, so nothing is missed.
installErrorReporting();

// The app shell is installable and works offline (vite-plugin-pwa, autoUpdate). Not on public token pages (guest RSVP,
// shared run sheet): someone opening a link without an account must not get a service worker or a cache on their device.
if (!isTokenPage(location.pathname) && "serviceWorker" in navigator) {
  void import("virtual:pwa-register")
    .then(({ registerSW }) => registerSW({ immediate: true, onRegisterError: (error: unknown) => reportError(error, "uncaught") }))
    .catch((error: unknown) => reportError(error, "uncaught"));
}

// A prerendered public page (scripts/prerender.mjs) arrives with the router's head tags already in place for
// crawlers. React is about to render the same tags; remove the snapshot's copies so there is exactly one of each.
document.head.querySelectorAll("[data-prerender]").forEach((el) => el.remove());

/** The API says the session is gone (signed out elsewhere, ended from another device, expired). */
const sessionEnded = (e: unknown) => e instanceof ApiError && e.status === 401 && e.code === "UNAUTHENTICATED";
let onSessionEnded: () => void = () => {};

/**
 * Every failed query and mutation passes here: an ended session inside the app goes back to sign-in, and a failure that
 * is not an API answer or a lost connection — a bug in a query function or a mutation's code — is reported.
 */
const onQueryError = (kind: "query" | "mutation") => (e: unknown) => {
  if (sessionEnded(e)) onSessionEnded();
  else reportError(e, kind);
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onQueryError("query") }),
  mutationCache: new MutationCache({ onError: onQueryError("mutation") }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Don't hammer the API on auth/permission errors — they won't heal by retrying.
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: true,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  scrollRestoration: true,
  // A screen that failed to render or load: its error boundary shows the error, the report goes to the server.
  defaultOnCatch: (error) => reportError(error, "render"),
});

// Reports name the screen by its route id (`/app/w/$weddingId/guests`): ids and link tokens never leave in a report.
router.subscribe("onResolved", () => setReportScreen(router.state.matches.at(-1)?.routeId));
// A public page in another language (`/de/…`, src/lib/locales.ts) says so to the browser, screen readers and search engines.
router.subscribe("onResolved", () => { document.documentElement.lang = LOCALE_TAG[currentLocale()]; });

// Inside /app, a session that ended server-side drops everything cached for the person and asks them to sign in again,
// coming back to the same page afterwards. Public pages make no signed-in requests, so they are left alone.
let ending = false;
onSessionEnded = () => {
  if (ending || !location.pathname.startsWith("/app")) return;
  ending = true;
  const back = `${location.pathname}${location.search}`;
  queryClient.clear();
  void router.navigate({ to: "/login", search: { redirect: back, reason: "session" }, replace: true }).finally(() => { ending = false; });
};

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const app = (
  <StrictMode>
    <ThemeProvider>
      <ConsentProvider>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <RouterProvider router={router} />
          </ToastProvider>
        </QueryClientProvider>
      </ConsentProvider>
    </ThemeProvider>
  </StrictMode>
);

// A translated public page renders in its language from the first paint, so its strings load before React starts. An
// English page loads nothing; a failed download renders English, and the `/$lang` route tries again on the next page.
const locale = currentLocale();
const strings = locale === "en" ? Promise.resolve() : import("./i18n/load-locale").then(({ loadLocale }) => loadLocale(locale));
void strings.catch(() => undefined).finally(() => createRoot(document.getElementById("root")!).render(app));
